import axios from 'axios';
import { endpoints, app } from '../config';
import FileHelper from '../helpers/FileHelper';

axios.defaults.withCredentials = true;

const API_BASE_URL = endpoints.backendApi.url;

const defaultFetchParams = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  credentials: 'include',
};

class BackendApiService {
  static handleApiFailure(error) {
    console.error(error);
    return false;
  }

  static async processApiResponseSingle(apiResponse, withFullResponse = false) {
    if (![200, 201].includes(apiResponse.status)) {
      if (withFullResponse) {
        try {
          const fullResponse = await apiResponse.json();
          return { ...fullResponse, success: false };
        } catch (e) {
          console.error(e);
        }
      }
      return false;
    }
    const fullResponse = await apiResponse.json();
    return fullResponse.data;
  }

  static async processApiResponseList(apiResponse) {
    if (apiResponse.status !== 200) {
      return {
        data: [],
        extra: {
          recordsTotal: 0,
          recordsFiltered: 0,
        },
      };
    }
    return await apiResponse.json();
  }

  static objToQueryString(obj) {
    const keyValuePairs = [];
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
      }
    }
    return keyValuePairs.join('&');
  }

  static async doLogout(domain) {
    const apiResponse = await fetch(`${API_BASE_URL}logout?domain=${domain}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getSessionData() {
    const apiResponse = await fetch(`${API_BASE_URL}session`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getTransferenceList(params) {
    const apiResponse = await fetch(`${API_BASE_URL}transferences?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async getTransferenceHistoryList(params) {
    const apiResponse = await fetch(`${API_BASE_URL}transferences/history?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async getFirmasDocumentosList(params) {
    const apiResponse = await fetch(`${API_BASE_URL}firmas-documentos?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async getTransferenceData(code) {
    const apiResponse = await fetch(`${API_BASE_URL}transferences/${code}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getTransferenceDataForNotaries(code) {
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const apiResponse = await fetch(`${API_BASE_URL}transferences/notary-transferences/${code}`, defaultFetchParams);
          const result = await this.processApiResponseSingle(apiResponse);
          resolve(result);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      }, app.msToRequestTransference);
    });
  }

  static async getFirmasDocumentosData(id, withConfig = false) {
    const apiResponse = await fetch(`${API_BASE_URL}firmas-documentos/${id}?withConfig=${withConfig}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getPreAdmissionList(params) {
    const apiResponse = await fetch(`${API_BASE_URL}pre-admissions?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async getPreAdmissionData(id) {
    const apiResponse = await fetch(`${API_BASE_URL}pre-admissions/${id}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getCommunesListForSelect(params) {
    const apiResponse = await fetch(`${API_BASE_URL}communes?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async rejectTransference(code, data) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify(data);
    const apiResponse = await fetch(`${API_BASE_URL}transferences/${code}/reject`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async approveTransference(code) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    const apiResponse = await fetch(`${API_BASE_URL}transferences/${code}/approve`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  /**
   * Representation flag for the approve gate (PTG-4700).
   *
   * PREDICATE PARITY: the backend derives `is_representation` from the SAME
   * `ca_with_power` predicate that feeds the "Representacion SI/NO" column of
   * the transference list. Do NOT swap this for the
   * `notarial_flow_representation` config already present on the detail
   * payload - it is an INDEPENDENT config, and using it would silently
   * desync this modal from that column.
   *
   * @param {string} code transference code
   * @returns {Promise<{is_representation: 0|1}|false>} `false` on any non-2xx.
   */
  static async getTransferenceRepresentation(code) {
    const apiResponse = await fetch(`${API_BASE_URL}transferences/${code}/representation`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async approveFirmasDocumentos(id) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    const apiResponse = await fetch(`${API_BASE_URL}firmas-documentos/${id}/approve`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async requestSignDataForTransference(code, isRepresentativeSignature = false) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({
      isRepresentativeSignature,
    });
    const apiResponse = await fetch(`${API_BASE_URL}sign/ticket/${code}`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async requestSignDataForDocument(signDocumentId) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    const apiResponse = await fetch(`${API_BASE_URL}sign-document/ticket/${signDocumentId}`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async requestNewSignOperation(documentDataList, urlOK, urlKO, isRepresentativeSignature = false) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({
      documentDataList,
      urlOK,
      urlKO,
      isRepresentativeSignature,
    });
    const apiResponse = await fetch(`${API_BASE_URL}sign/operation`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async requestNewSignDocumentOperation(documentDataList, urlOK, urlKO) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({
      documentDataList,
      urlOK,
      urlKO,
    });
    const apiResponse = await fetch(`${API_BASE_URL}sign-document/operation`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getSignOperationStatus(id) {
    const apiResponse = await fetch(`${API_BASE_URL}sign/operation/${id}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async getSignOperationDocumentStatus(id) {
    const apiResponse = await fetch(`${API_BASE_URL}sign-document/operation/${id}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async completeSignTransference(code, operationId, isRepresentativeSignature = false) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({
      isRepresentativeSignature,
    });
    const apiResponse = await fetch(`${API_BASE_URL}sign/complete/${operationId}/${code}`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async completeSignDocument(signDocumentId, operationId) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    const apiResponse = await fetch(`${API_BASE_URL}sign-document/complete/${operationId}/${signDocumentId}`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async destroySignOperation(operationId) {
    const postData = { ...defaultFetchParams };
    postData.method = 'delete';
    const apiResponse = await fetch(`${API_BASE_URL}sign/operation/${operationId}`, postData);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async assignMasiveRepertory(repertories, updatedStatusFunction = null) {
    let currenctExecuted = [];
    let assignInProcess = 0;
    let transferencesInProcess = [];
    const maxExecutedNumber = 2;
    let countOK = 0;
    let countFail = 0;
    let fails = [];
    let results = {};
    for (let i = 0; i < repertories.length; i++) {
      results[repertories[i].transferenceCode] = -1;
      if (assignInProcess < maxExecutedNumber) {
        transferencesInProcess.push(repertories[i].transferenceCode);
        currenctExecuted.push(
          this.assignSimpleRepertory(repertories[i].transferenceCode, repertories[i].repertoryNumber),
        );
        assignInProcess++;
      }
      if (assignInProcess === maxExecutedNumber) {
        this.notifyStatusResult(results, updatedStatusFunction);
        const assignResult = await this.processMasiveAssignResponse(currenctExecuted, transferencesInProcess);

        countOK += assignResult.countOK;
        countFail += assignResult.countFail;
        fails = fails.concat(assignResult.fails);
        results = this.updateResultStatus(results, assignResult);
        currenctExecuted = [];
        transferencesInProcess = [];
        assignInProcess = 0;
        this.notifyStatusResult(results, updatedStatusFunction);
      }
    }
    if (assignInProcess > 0) {
      this.notifyStatusResult(results, updatedStatusFunction);
      const assignResult = await this.processMasiveAssignResponse(currenctExecuted, transferencesInProcess);
      countOK += assignResult.countOK;
      countFail += assignResult.countFail;
      fails = fails.concat(assignResult.fails);
      results = this.updateResultStatus(results, assignResult);
      this.notifyStatusResult(results, updatedStatusFunction);
    }
    return {
      countFail,
      countOK,
      fails,
    };
  }

  static notifyStatusResult(results, updatedStatusFunction) {
    if (typeof updatedStatusFunction === 'function') {
      updatedStatusFunction(results);
    }
  }

  static updateResultStatus(results, assignResult) {
    for (const pos in results) {
      if (Object.prototype.hasOwnProperty.call(results, pos) && results[pos] === -1) {
        if (assignResult.fails.includes(pos)) {
          results[pos] = 0;
        } else {
          results[pos] = 1;
        }
      }
    }
    return results;
  }

  static async assignSimpleRepertory(code, repertory) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({
      repertories: [
        {
          transferenceCode: code,
          repertoryNumber: repertory,
        },
      ],
    });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}transferences/repertory/masive`, postData);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async processMasiveAssignResponse(currenctExecuted, transferencesInProcess) {
    let countOK = 0;
    let countFail = 0;
    let fails = [];
    const responseExecution = await Promise.all(currenctExecuted);
    responseExecution.forEach((assignMasiveResponse, index) => {
      if (assignMasiveResponse === false) {
        countFail++;
        fails.push(transferencesInProcess[index]);
      } else {
        countOK += assignMasiveResponse.countOK;
        countFail += assignMasiveResponse.countFail;
        fails = fails.concat(assignMasiveResponse.fails);
      }
    });
    return {
      countFail,
      countOK,
      fails,
    };
  }

  static async savePreAdmisionManual(data) {
    const especificParams = {
      method: 'POST',
      body: JSON.stringify(data),
    };
    const requestParams = { ...defaultFetchParams, ...especificParams };
    const apiResponse = await fetch(`${API_BASE_URL}pre-admissions`, requestParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async registerPreAdmision(transferenceCode, withFullResponse = false) {
    const especificParams = {
      method: 'POST',
    };
    const requestParams = { ...defaultFetchParams, ...especificParams };
    const apiResponse = await fetch(`${API_BASE_URL}transferences/${transferenceCode}/pre-admission?auto-register=true`, requestParams);
    return await this.processApiResponseSingle(apiResponse, withFullResponse);
  }

  static async updatePreAdmision(id, data) {
    const especificParams = {
      method: 'PUT',
      body: JSON.stringify(data),
    };
    const requestParams = { ...defaultFetchParams, ...especificParams };
    const apiResponse = await fetch(`${API_BASE_URL}pre-admissions/${id}`, requestParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async generatePDFToRegCivil(ids, type = 'join_pdf') {
    const especificParams = {
      method: 'POST',
      body: JSON.stringify({
        ids,
        type,
      }),
    };
    const requestParams = { ...defaultFetchParams, ...especificParams };
    const apiResponse = await fetch(`${API_BASE_URL}transferences/generate-regcivil-pdf`, requestParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async findSignNotaries() {
    const apiResponse = await fetch(`${API_BASE_URL}sign-notaries`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async findSignatureRepresentative() {
    const apiResponse = await fetch(`${API_BASE_URL}sign-representative`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async findNotaryWorkSummary() {
    const apiResponse = await fetch(`${API_BASE_URL}notaries/summary`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async updateNotarySign(params) {
    const requestParams = { ...defaultFetchParams, method: 'POST', body: JSON.stringify(params) };
    const apiResponse = await fetch(`${API_BASE_URL}change-notary-sign`, requestParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async setTagEnteredToCivilregister(params) {
    const requestParams = { ...defaultFetchParams, method: 'PUT', body: JSON.stringify(params) };
    const apiResponse = await fetch(`${API_BASE_URL}transferences/notary-tag`, requestParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async uploadRequest(firstRepertory, lastRepertory, file) {
    try {
      const s3FormData = new FormData();

      const presignedResponse = await axios.post(`${API_BASE_URL}presigned-request`);
      console.log(presignedResponse);
      const presignedResponseData = presignedResponse.data;
      if (presignedResponseData.status === 'OK') {
        const responseFields = presignedResponseData.data.fields;
        for (const key in responseFields) {
          s3FormData.append(key, responseFields[key]);
        }
        s3FormData.append('file', file);
        await axios.post(presignedResponseData.data.url, s3FormData);
        const uploadResponse = await axios.post(`${API_BASE_URL}upload-request`, {
          file_url: `${presignedResponseData.data.url}/${responseFields.key}`,
          first_repertory: firstRepertory,
          last_repertory: lastRepertory,
        });
        if (uploadResponse.data.status === 'OK') {
          return true;
        }
        console.log(uploadResponse.data.message);
      } else {
        console.log(presignedResponseData.message);
      }
    } catch (e) {
      console.log(e);
    }
    return false;
  }

  static async uploadUpdatedMandatePhysic(signId, subsidiaryId, code, file) {
    try {
      const s3FormData = new FormData();

      const presignedResponse = await axios.post(`${API_BASE_URL}firmas-documentos/${signId}/pre-signed`, {
        subsidiaryId,
        code,
      });
      const presignedResponseData = presignedResponse.data;
      if (presignedResponseData.status === 'OK') {
        const responseFields = presignedResponseData.data.fields;
        for (const key in responseFields) {
          s3FormData.append(key, responseFields[key]);
        }
        s3FormData.append('file', file);
        await axios.post(presignedResponseData.data.url, s3FormData);
        const uploadResponse = await axios.put(`${API_BASE_URL}firmas-documentos/${signId}`, {
          file_url: `${presignedResponseData.data.url}/${responseFields.key}`,
          code,
        });
        if (uploadResponse.data.status === 'OK') {
          return true;
        }
        console.log(uploadResponse.data.message);
      } else {
        console.log(presignedResponseData.message);
      }
    } catch (e) {
      console.log(e);
    }
    return false;
  }

  /**
     * Pre admision for all transference received
     *
     * @param {*} transferenceCode Array with transferences codes
     * @param {*} withFullResponse Boolean for full or not response
     */
  static async registerPreAdmisionFull(withFullResponse = false) {
    const requestParams = {
      ...defaultFetchParams,
      method: 'POST',
    };

    const apiResponse = await fetch(`${API_BASE_URL}pre-admissions/send-queue-full`, requestParams);
    console.log('apiResponse');
    console.log(apiResponse);
    return await this.processApiResponseSingle(apiResponse, withFullResponse);
  }

  static async uploadedRequests(params) {
    const apiResponse = await fetch(`${API_BASE_URL}upload-request?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async rejectDocument(id, comment) {
    const requestParams = {
      ...defaultFetchParams,
      method: 'POST',
      body: JSON.stringify({ comment }),
    };
    const apiResponse = await fetch(
      `${API_BASE_URL}firmas-documentos/${id}/reject-sign-document`,
      requestParams,
    );
    return await this.processApiResponseList(apiResponse);
  }

  static async getRejectionList() {
    const apiResponse = await axios.get(`${API_BASE_URL}transferences/notary-rejection`);
    return apiResponse;
  }

  static async SendExternalRepertoriesRequest(transferIds) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({ transferIds });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}transferences/request-extern-repertory`, postData);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async GetExternalRepertoryRequest(transfers) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({ transfers });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}transferences/check-extern-repertory`, postData);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getNotaryUsersList() {
    const apiResponse = await fetch(`${API_BASE_URL}notaries/users-list`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async CAVRequest(plate, pretransferCode) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({ plate, pretransferCode });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/cav-request`, postData);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async GetCAVRequestStatus(ticket) {
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/cav-request/status/${ticket}`, defaultFetchParams);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getReport(code) {
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/report/${code}`, defaultFetchParams);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getFiscalValuation(plate) {
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/fiscal-valuation/${plate}`, defaultFetchParams);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getFiscalValuationAlt(params) {
    const { year, brand, model } = params;
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({ year, brand, model });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/backup-fiscal-valuation`, postData);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async uploadFile(file, code, type) {
    try {
      const base64File = await FileHelper.getBase64(file);
      const data = {
        code,
        mimeType: file.type,
        file: base64File,
        type: type || 'permiso-circulacion',
      };
      const uploadFile = await axios.post(`${API_BASE_URL}notary-contracts/upload`, data);
      return uploadFile.data.data;
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async downloadFile(fileUrl) {
    try {
      const apiResponse = await axios.post(`${API_BASE_URL}notary-contracts/download-file`, { fileUrl });
      return apiResponse;
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPensionDebtor(ruts) {
    const postData = { ...defaultFetchParams };
    postData.method = 'post';
    postData.body = JSON.stringify({ personRuts: ruts });
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/pension-debtors`, postData);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async createPretransfer(data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/create-pretransfer`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPretransfer(code) {
    const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/get-pretransfer/${code}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async updateNotaryTransfer(code, updatedData) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ code, data: updatedData }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/update-pretransfer`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPersonByRut(rut) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ rut }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/get-person`, requestParams);
      const response = await this.processApiResponseSingle(apiResponse);
      if (response) {
        return {
          name: response.nombre || '',
          fatherLastName: response.apellido_paterno || '',
          motherLastName: response.apellido_materno || '',
          sex: response.sexo || '',
          phone: response.celular ? `+${response.celular}` : '',
          email: response.email || '',
          nationality: response.nacionalidad || '',
          maritalStatus: response.estado_civil || '',
          occupation: response.profesion || '',
          address: {
            street: response.direccion || '',
            number: response.direccion_calle_numero || '',
            rest: response.direccion_resto || '',
            regionId: response.region_idregion || '',
            comunaId: response.comuna_idcomuna || '',
          },
        };
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  static async getCommuneListByRegion(id) {
    if (id) {
      const apiResponse = await fetch(`${API_BASE_URL}communes/${id}`, defaultFetchParams);
      return await this.processApiResponseSingle(apiResponse);
    }
    return [];
  }

  static async getRegionsList() {
    const apiResponse = await fetch(`${API_BASE_URL}regions`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async createTransference(code, data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/create-transfer/${code}`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async cancelTransference(code, comment = '') {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ comment }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}transferences/${code}/cancel`, requestParams);
      return apiResponse;
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPretransferByTransfer(transferId) {
    const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/get-pretransfer-by-transfer/${transferId}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async updateTransfer(code, data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/update-transference/${code}`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async removeTransferBuyer(transferCode, userId) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ code: transferCode, userId }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/remove-buyers`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async addTransferBuyer(transferCode, data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ code: transferCode, data }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/modify-buyers`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async startSignContrat(data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/start-sign`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async checkSign(code) {
    try {
      const response = await fetch(`${API_BASE_URL}notary-contracts/check-sign/${code}`, defaultFetchParams);
      return await this.processApiResponseSingle(response);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async removeSignature(transferCode, data) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/delete-signature/${transferCode}`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getCAVURL(ticket) {
    try {
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/url-cav/${ticket}`, defaultFetchParams);
      return this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPretransferenceList(params) {
    const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/pretransfer-list?${this.objToQueryString(params)}`, defaultFetchParams);
    return await this.processApiResponseList(apiResponse);
  }

  static async approveTransfer(code) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ code }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/approve-transference`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getAllCountries() {
    const apiResponse = await fetch(`${API_BASE_URL}countries`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async archivePretransfer(code) {
    try {
      const especificParams = {
        method: 'POST',
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/archive-pretransference/${code}`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async getPeople(ruts) {
    const especificParams = {
      method: 'POST',
      body: JSON.stringify({ ruts }),
    };
    const requestParams = { ...defaultFetchParams, ...especificParams };
    const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/get-people`, requestParams);
    const people = await this.processApiResponseSingle(apiResponse);

    if (people && Array.isArray(people)) {
      const peopleArray = people.map((person) => ({
        identityCode: person.rut,
        name: person.nombre || '',
        secondName: person.segundo_nombre || '',
        fullName: person.nombre_completo || '',
        fatherLastName: person.apellido_paterno || '',
        motherLastName: person.apellido_materno || '',
        sex: person.sexo || '',
        phone: person.celular ? `+${person.celular}` : '',
        email: person.email || '',
        nationality: person.nacionalidad || '',
        maritalStatus: person.estado_civil || '',
        occupation: person.profesion || '',
        address: {
          street: person.direccion || '',
          number: person.direccion_calle_numero || '',
          rest: person.direccion_resto || '',
          regionId: person.region_idregion || '',
          comunaId: person.comuna_idcomuna || '',
        },
      }));

      return peopleArray;
    }
    return [];
  }

  static async createOrUpdatePerson(data) {
    try {
      const bodyData = {
        rut: data.identityCode,
        ...(data.name && { nombre: data.name }),
        ...(data.fatherLastName && { apellido_paterno: data.fatherLastName }),
        ...(data.motherLastName && { apellido_materno: data.motherLastName }),
        ...(data.segundo_nombre && { segundo_nombre: data.segundo_nombre }),
        ...(data.fullName && { nombre_completo: data.fullName }),
        ...(data.email && { email: data.email }),
        ...(data.phone && { celular: data.phone }),
        ...(data.sex && { sexo: data.sex }),
        ...(data.address?.street && { direccion: data.address?.street }),
        ...(data.address?.number && { direccion_calle_numero: data.address?.number }),
        ...(data.addres?.rest && { direccion_resto: data.addres?.rest }),
        ...(data.address?.regionId && { region_idregion: data.address?.regionId }),
        ...(data.address?.comunaId && { comuna_idcomuna: data.address?.comunaId }),
        ...(data.nationality && { nacionalidad: data.nationality }),
        ...(data.estado_civil && { estado_civil: data.estado_civil }),
        ...(data.profesion && { profesion: data.profesion }),
      };

      const especificParams = {
        method: 'POST',
        body: JSON.stringify(bodyData),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/modify-person`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }

  static async updateMultiplePersons(transferenceCode, data) {
    try {
      const especificParams = {
        method: 'PATCH',
        body: JSON.stringify(data),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/update-multiple-persons/${transferenceCode}`, requestParams);
      if (apiResponse.status === 200 && apiResponse.ok) {
        const response = await apiResponse.json();
        return response;
      }
      const errorResponse = await apiResponse.json();
      console.error('Error:', errorResponse);
      return false;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  static async getSocietyByUserAndSubsidiary(userId, subsidiaryId) {
    const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/get-society-by-user-and-subsidiary/${userId}/${subsidiaryId}`, defaultFetchParams);
    return await this.processApiResponseSingle(apiResponse);
  }

  static async sendToUAFROEExcelDownload(startDate, endDate) {
    try {
      const especificParams = {
        method: 'POST',
        body: JSON.stringify({ startDate, endDate }),
      };
      const requestParams = { ...defaultFetchParams, ...especificParams };
      const apiResponse = await fetch(`${API_BASE_URL}notary-contracts/uaf-roe-excel-download`, requestParams);
      return await this.processApiResponseSingle(apiResponse);
    } catch (e) {
      return this.handleApiFailure(e);
    }
  }
}

export default BackendApiService;
